import React from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { markdownComponents, type TaskMarkdownStyle } from './TaskMarkdown';

/**
 * `TaskMarkdown` plus KaTeX rendering of `$…$` / `$$…$$`.
 *
 * A separate module for the same reason as the MathJax variant: KaTeX is a
 * heavy dependency, and only the open-problems family renders math this way.
 * The component table is still defined once, in `TaskMarkdown`.
 *
 * The KaTeX stylesheet is imported once at the app root (`src/index.tsx`).
 */
export interface TaskMarkdownKatexProps extends TaskMarkdownStyle {
  children: string;
  className?: string;
  components?: Partial<Components>;
}

export function TaskMarkdownKatex({
  children, className, components, tone, scale,
}: TaskMarkdownKatexProps) {
  const resolved = React.useMemo(
    () => ({ ...markdownComponents({ tone, scale }), ...components }),
    [tone, scale, components],
  );
  const body = (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkMath]}
      rehypePlugins={[rehypeKatex]}
      components={resolved}
    >
      {children}
    </ReactMarkdown>
  );
  return className ? <div className={className}>{body}</div> : body;
}
